<template lang="pug">
li.cart-line-item(:class="line != 1 ? 'cart-line-item--separated' : null")
  .cart-line-item__image-container
    img.cart-line-item__image(:src="item.image | image('200x200')" :alt="item.product_title")
  .cart-line-item__details
    h3
      a.link.link--primary.cart-line-item__product-title(:href="item.url" v-html="item.product_title")
    button.button--icon.cart-line-item__remove(@click="removeItem", :tabindex="0", :aria-label="`Remove Item ${item.title}`")
      .u-sr-only Remove Item
      icon(name="close" height="12px" width="12px")
    p.cart-line-item__variant-name(v-if="item.variant_title && item.variant_title.toLowerCase() != 'default title'") {{ item.variant_title }}
    p.cart-line-item__selling-plan-info(v-if="item.selling_plan_allocation" v-text="item.selling_plan_allocation.selling_plan.name")
    p.cart-line-item__product-price(v-if="!isGiftProduct") #[span.sr_only Each {{ item.title }} is] {{ item.price | moneyWithoutDecimals }}
    p.cart-line-item__product-price.cart-line-item__product-price--discounted(v-else)
      span {{ item.price | moneyWithoutDecimals }}
      span - FREE
    .cart-line-item__actions(v-if="!isGiftProduct")
      .cart-line-item__price #[span.sr_only The total for {{ item.title }} is] {{ item.line_price | moneyWithoutDecimals }}
      .cart-line-item__quantity
        label(for="quantity") Quantity
        quantity-input.pdp-form__quantity(:item="item" ,:quantity.sync="quantity" :min="0")
</template>

<script>
  import Icon from 'scripts/components/basic/Icon.vue';
import QuantityInput from 'scripts/components/forms/QuantityInput.vue';

  export default {
    name: 'CartLineItem',
    components: { Icon, QuantityInput },
    props: {
      item: Object,
      line: Number,
    },
    data() {
      return {
        quantity: this.item.quantity,
      };
    },
    watch: {
      quantity(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.updateItem();
        }
      },
    },
    computed: {
      isGiftProduct() {
        return this.item.properties && this.item.properties.threshold === 'threshold'
      }
    },
    methods: {
      updateItem() {
        if (this.quantity > 0) {
          this.$store.dispatch('cart/updateItem', { line: this.line, quantity: this.quantity });
        } else {
          this.removeItem();
        }
      },
      removeItem() {
        this.$store.dispatch('cart/removeItem', this.line);
        this.triggerRemoveItemEvent(this.item);
      },
      triggerRemoveItemEvent(item) {
        // fetch variant barcode
        const getProductExtraData = function(callback) {
          fetch('/api/2023-04/graphql.json', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': 'd3e0a78585513213d11e1ca457ee6e93' // public token from headless app
            },
            body: `{"query":"{product(id: \\"gid://shopify/Product/${item.product_id}\\") {gm_product_id: metafield(namespace: \\"custom_fields\\", key: \\"gm_product_id\\") {value} variants(first: 20) {edges {node {id barcode}}}}}"}`
          }).then((data) => data.json()).then((data) => {
            // console.log(data)
            for (const v of data.data.product.variants.edges) {
              if (v.node.id == `gid://shopify/ProductVariant/${item.variant_id}`) {
                callback(v.node.barcode, data.data.product.gm_product_id.value);
              }
            }
          });
        }

        getProductExtraData((barcode, gm_product_id) => {
          window.getCustomerGuid((customer_guid) => {
            let removeItemPayload = {
              'event': 'remove_from_cart',
              'user_id': customer_guid,
              'event_id': new Date().getTime() + '.' + Math.random().toString(36).substring(5),
              'ecommerce': {
                'currencyCode': window.BVA.shop.currencyCode,
                'remove': {
                  'actionField': {'list': 'slideout_cart'},
                  'products': [{
                    'id': barcode,
                    'sku': item.sku.replace("'",""),
                    'handle': item.handle,
                    'productId': item.product_id,
                    'variantId': item.variant_id,
                    'name' : item.product_title.replace("'", ""),
                    'price': (item.price / 100),
                    'brand' : item.vendor.replace("'", ""),
                    'variant': item.title.replace("'", ""),
                    'quantity': item.quantity,
                    'category': item.gm_product_id
                  }]
                },
                'items': [
                  {
                    item_id: barcode,
                    gm_product_id: gm_product_id,
                    shopify_product_id: item.product_id,
                    shopify_variant_id: item.variant_id,
                    shopify_sku: item.sku.replace("'", ""),
                    shopify_handle: item.handle,
                    item_name : item.product_title.replace("'", ""),
                    affiliation: window.BVA.shop.name,
                    currency: window.BVA.shop.currencyCode,
                    item_brand : item.vendor.replace("'", ""),
                    item_category: gm_product_id,
                    item_list_id: 'slideout_cart',
                    item_list_name: 'slideout_cart',
                    item_variant: item.title.replace("'",""),
                    price: (item.price / 100)
                  }
                ]
              }
            }

            item.options_with_values.forEach(({ name, value }) => {
              removeItemPayload.ecommerce.items[0][name] = value;
            });

            dataLayer.push({ ecommerce: null });
            dataLayer.push(removeItemPayload);
            console.log('event: remove-item-event', removeItemPayload);
          });
        });
      }
    }
  };
</script>

<style scoped lang="scss">
  .cart-line-item {
    display: flex;
    position: relative;
    padding: 20px 0;
    color: $color-black;
  }
  .cart-line-item--separated {
    border-top: 1px solid $color-black;
  }
  .cart-line-item__image-container {
    width: 100%;
    max-width: 85px;
  }
  .cart-line-item__image {
    object-fit: cover;
    display: block;
    height: auto;
    width: 100%;
    object-position: bottom;
    overflow: hidden;
    background: $color-grey-100;
  }
  .cart-line-item__product-title {
    color: var(--color-navy-500);
    font-size: rem(16);
    line-height: rem(20);
    margin-right: 20px;
    text-decoration: none;
    margin-bottom: 0;
    border-bottom: 0;
    padding-bottom: 0;

    &:hover,
    &:active {
      color: $color-black;
    }
  }
  .cart-line-item__variant-name {
    color: var(--color-slate-500);
    font-size: rem(12px);
    text-transform: uppercase;
    margin-bottom: 0;
    letter-spacing: 1px;
  }
  .cart-line-item__selling-plan-info {
    color: var(--color-slate-500);
    font-size: rem(14px);
    margin-bottom: 0;
  }
  .cart-line-item__product-price {
    font-size: rem(17);
    margin: rem(8px) 0 0 0;
    color: $color-slate-500;
  }
  .cart-line-item__product-price--discounted {
    span:first-child {
      text-decoration: line-through;
    }
     span:last-child {
      margin-left: 5px
    }
  }
  .cart-line-item__details {
    flex-grow: 1;
    padding: 0 0 0 20px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .cart-line-item__actions {
    display: flex;
    align-items: center;
    margin-top: rem(10px);
    flex-direction: row-reverse;
    justify-content: space-between;
    .loading {
      width: 20px;
      height: 20px;
    }
  }
  .cart-line-item__quantity {
    width: auto;
  }
  .cart-line-item__remove {
    align-items: flex-start;
    position: absolute;
    top: 20px;
    right: 0;
    font-size: rem(9);
    text-transform: uppercase;
    color: $color-black;
    background: transparent;
    border: 0;
    padding: 0;
    min-width: auto;
  }
  .cart-line-item__product-price--compare-at {
    margin: 0 5px;
    color: $color-grey-900;
  }
  .u-sr-only {
    border: 0;
    clip: rect(0 0 0 0);
    height: auto;
    margin: 0;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;
    white-space: nowrap;
  }
  .cart-line-item__price {
    color: $color-slate-500;
  }
</style>
